import { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { ThemeContext } from 'styled-components';
import { status } from 'constants/general';
import Loader from 'modules/common/components/Loader';
import {
  invoiceGridItemsToSizeMap,
  orderInvoiceGridHeaderKeys,
  orderInvoiceGridHeaderList,
  TABLE_VIEWS,
} from 'modules/orders/OrdersView/OrdersContainer/OrderDetailsContainer/OrderDetailsComponent/helper';
import { useMedicalInsuranceDiscountCalculate } from 'modules/orders/hooks/useMedicalInsuranceDiscountCalculate';
import { useSelfInsuranceInvoice } from '../../useSelfInsuranceOrder';

import SearchInStock from './OrderInvoiceComponents/SearchInStock';
import { InvoiceSummary } from './OrderInvoiceComponents/InvoiceSummary';
import InvoiceItemsBlock, { selfInsuranceBlockTabsContainerStyle } from './InvoiceItemsBlock';

import {
  OrderDetailsInvoiceSection,
  OrderDetailsInvoiceTitle,
  OrderDetailsInvoiceSearchBarWrapper,
  OrderDetailsInvoiceSearchBarInputWrapper,
  OrderDetailsInvoiceSearchBar,
  OrderDetailsInvoiceSearchBarIcon,
  OrderDetailsInvoiceSearchBarEnvelop,
  extendOrderDetailsInvoiceLoaderStyle,
} from './OrderInvoice.style';

const OrderInvoice = ({
  stringsData,
  orderDetails,
  orderServicesSuggestions,
  getItemsSuggestionsData,
  resetServicesSuggestionsData,
  orderStatusToEnableEditInvoice,
  orderStatusToEnableAddingPromoCode,
  objectMapper,
  dispensedOrderItems,
  setDispensedOrderItems,
  handleInvoiceItemCheckToBeConfirmed,
  checkedItemToBeConfirmed,
  isEligableForAlternativeRecommendation,
  setSuggestAllternativeModal,
  productsAlternatives,
  orderTableMode,
  setOrderTableMode,
  appendProductAlternatives,
  onRemoveProductAlternatives,
  onMedicalInsuranceDiscountCalculateResult,
  insuranceRxLineDiscountLookup,
  onMoveInsuranceItemToAdditional,
}) => {
  const themeContext = useContext(ThemeContext);
  const orderData = orderDetails.details;
  const { alternativesRecommendations, orderReceipt } = orderData;

  const suggestAlternativesTabEnabled =
    Boolean(alternativesRecommendations?.length) || isEligableForAlternativeRecommendation;

  const invoiceHeaderMenuItemsObjIntialValue = {
    original: stringsData.orderDetailsContent.orderInvoiceFilterOriginal,
    dispensed: stringsData.orderDetailsContent.orderInvoiceFilterDispensed,
  };

  const selectedFilter = invoiceHeaderMenuItemsObjIntialValue.original;
  const [searchInputQuery, setSearchInputQuery] = useState('');
  const [isEnvelopOpen, setIsEnvelopOpen] = useState(false);
  const [promoCodeReference, setPromoCode] = useState('');
  const [isPromoCodeInputAvailable, setIsPromoCodeInputAvailable] = useState(false);
  const [insuranceBlockTableMode, setInsuranceBlockTableMode] = useState(
    TABLE_VIEWS.ORDER_PREPARATION,
  );
  const [additionalBlockTableMode, setAdditionalBlockTableMode] = useState(
    TABLE_VIEWS.ORDER_PREPARATION,
  );

  const listListenerHandler = event => {
    const myElementToCheckIfClicksAreInsideOf = document.querySelector('#search-items');
    if (
      myElementToCheckIfClicksAreInsideOf &&
      !myElementToCheckIfClicksAreInsideOf.contains(event.target)
    ) {
      setIsEnvelopOpen(false);
    }
  };

  useEffect(() => {
    if (orderServicesSuggestions && orderServicesSuggestions?.length) {
      setIsEnvelopOpen(true);
    }
  }, [orderServicesSuggestions]);

  useEffect(() => {
    document.body.addEventListener('click', listListenerHandler, true);
    return () => {
      document.body.removeEventListener('click', listListenerHandler, true);
    };
  }, []);

  useEffect(() => {
    const mappedServicesListUpdated = orderDetails?.details?.mappedItemsList;
    setDispensedOrderItems(mappedServicesListUpdated);
    setPromoCode(orderDetails?.details?.promoCode);
    if (orderDetails?.details?.promoCode) setIsPromoCodeInputAvailable(false);
  }, [orderDetails, setDispensedOrderItems]);

  useEffect(() => {
    if (promoCodeReference === '') setIsPromoCodeInputAvailable(true);
  }, [promoCodeReference]);

  const orderDetailsKey = orderDetails?.details?.key;
  useEffect(() => {
    setInsuranceBlockTableMode(TABLE_VIEWS.ORDER_PREPARATION);
    setAdditionalBlockTableMode(TABLE_VIEWS.ORDER_PREPARATION);
  }, [orderDetailsKey]);

  const {
    orderStatus,
    enableEditInvoice,
    insuranceInvoiceRows,
    additionalInvoiceRows,
    isDispensedOrderItemsArray,
    isSelfInsuranceOrder,
    showInsuranceBlock,
    showAdditionalBlock,
    enableEditInsuranceBlock,
    enableEditAdditionalBlock,
    enableInsuranceAlternativesTab,
    enableAdditionalAlternativesTab,
    insuranceTableViewMode,
    additionalTableViewMode,
    enableSearchItemsInOrderPreparation: enableSelfInsuranceSearchItemsInOrderPreparation,
    showSelfInsuranceInvoiceSummaryAtBottom,
  } = useSelfInsuranceInvoice({
    orderDetails,
    dispensedOrderItems,
    orderStatusToEnableEditInvoice,
    insuranceBlockTableMode,
    additionalBlockTableMode,
  });

  useMedicalInsuranceDiscountCalculate({
    orderDetails,
    dispensedOrderItems,
    productsAlternatives,
    isDispensedOrderItemsArray,
    onMedicalInsuranceDiscountCalculateResult,
    delayMs: 450,
  });

  const servicesSearchBarTimeout = useRef(null);

  const getServicesSuggestions = query => {
    setSearchInputQuery(query);

    if (servicesSearchBarTimeout.current) {
      clearTimeout(servicesSearchBarTimeout.current);
    }

    if (query && query.length >= 2) {
      servicesSearchBarTimeout.current = setTimeout(() => {
        clearTimeout(servicesSearchBarTimeout.current);

        getItemsSuggestionsData(query);
      }, 300);
    }
  };

  const handleServicesSuggestionsInputChange = event => {
    const { value } = event.target;

    if (value.length > 30) return;
    setSearchInputQuery(value);

    if (value && value.length >= 2) {
      setIsEnvelopOpen(true);
      getServicesSuggestions(value);
    } else {
      setIsEnvelopOpen(false);
      resetServicesSuggestionsData();
    }
  };

  const handleServicesSuggestionsItemClick = item => {
    if (dispensedOrderItems.some(service => service.serviceKey === item.productKey)) return;
    setIsEnvelopOpen(false);
    setSearchInputQuery('');
    resetServicesSuggestionsData();
    setDispensedOrderItems(prev => [
      ...prev,
      {
        mainImageUrl: item.mainImageUrl,
        serviceKey: item.productKey,
        serviceName: item.productNameEn,
        servicePrice: item.newPrice,
        price: item.newPrice,
        quantity: 1,
        productShapeId: item.id,
        productShapeName: item.productShapeTypeName,
      },
    ]);
  };

  const handleServiceItemDeleteBtnClick = index => {
    const tempDispensedOrderItems = _.cloneDeep(dispensedOrderItems);
    const temp = tempDispensedOrderItems.filter((_item, itemIndex) => itemIndex !== index);
    setDispensedOrderItems(temp);
  };

  const handleChangeItemQuantity = (itemKey, index, operationType) => {
    const tempDispensedOrderItems = _.cloneDeep(dispensedOrderItems);

    const tempItemIndex = tempDispensedOrderItems.findIndex(
      (service, itemIndex) => service.serviceKey === itemKey && itemIndex === index,
    );
    const tempItem = tempDispensedOrderItems.find(
      (service, itemIndex) => service.serviceKey === itemKey && itemIndex === index,
    );

    if (operationType === 'plus') tempItem.quantity += 1;
    else if (operationType === 'minus' && tempItem.quantity > 1) tempItem.quantity -= 1;

    tempDispensedOrderItems[tempItemIndex] = { ...tempItem };
    setDispensedOrderItems([...tempDispensedOrderItems]);
  };

  if (dispensedOrderItems === status.FETCHING)
    return (
      <OrderDetailsInvoiceSection padding>
        <Loader
          width={50}
          height={50}
          thickness={6}
          extendStyle={extendOrderDetailsInvoiceLoaderStyle}
        />
      </OrderDetailsInvoiceSection>
    );

  if (dispensedOrderItems === status.FAIL)
    return (
      <OrderDetailsInvoiceSection padding>
        <OrderDetailsInvoiceTitle>
          {stringsData.orderDetailsContent.noDataPlaceholder}
        </OrderDetailsInvoiceTitle>
      </OrderDetailsInvoiceSection>
    );

  const enableSuggestAlternativesTab = suggestAlternativesTabEnabled;

  const handleGlobalAlternativesTabChange = mode => {
    setOrderTableMode(mode);
    appendProductAlternatives([]);
  };

  const invoiceTableContainerCommonProps = {
    orderInvoiceGridHeaderList,
    orderInvoiceGridHeaderKeys,
    orderDetails,
    invoiceHeaderMenuItemsObj: invoiceHeaderMenuItemsObjIntialValue,
    invoiceGridItemsToSizeMap,
    handleServiceItemDeleteBtnClick,
    handleChangeItemQuantity,
    orderStatusToEnableEditInvoice,
    objectMapper,
    handleInvoiceItemCheckToBeConfirmed,
    checkedItemToBeConfirmed,
    setSuggestAllternativeModal,
    productsAlternatives,
    alternativesRecommendations,
    onRemoveProductAlternatives,
    insuranceRxLineDiscountLookup,
    onMoveInsuranceItemToAdditional,
    invoiceZeroPriceAlternativesHint:
      stringsData.orderDetailsContent.orderInvoiceZeroPriceRequiresAlternativesHint,
  };

  const insuranceBlockSwitcher = {
    enabled: enableInsuranceAlternativesTab,
    value: insuranceBlockTableMode,
    onChange: setInsuranceBlockTableMode,
    tabsContainerStyle: selfInsuranceBlockTabsContainerStyle,
  };

  const additionalBlockSwitcher = {
    enabled: enableAdditionalAlternativesTab,
    value: additionalBlockTableMode,
    onChange: setAdditionalBlockTableMode,
    tabsContainerStyle: selfInsuranceBlockTabsContainerStyle,
  };

  let invoiceMainItems = null;
  if (isDispensedOrderItemsArray && dispensedOrderItems.length > 0) {
    if (isSelfInsuranceOrder) {
      invoiceMainItems = (
        <>
          {showInsuranceBlock && (
            <InvoiceItemsBlock
              title="Insurance order / E-prescription"
              switcher={insuranceBlockSwitcher}
              tableProps={{
                ...invoiceTableContainerCommonProps,
                dispensedOrderItems: insuranceInvoiceRows,
                orderTableMode: insuranceTableViewMode,
                enableEditInvoice: enableEditInsuranceBlock,
              }}
            />
          )}
          {showAdditionalBlock && (
            <InvoiceItemsBlock
              title="Additional items"
              switcher={additionalBlockSwitcher}
              tableProps={{
                ...invoiceTableContainerCommonProps,
                dispensedOrderItems: additionalInvoiceRows,
                orderTableMode: additionalTableViewMode,
                enableEditInvoice: enableEditAdditionalBlock,
              }}
            />
          )}
        </>
      );
    } else {
      invoiceMainItems = (
        <InvoiceItemsBlock
          switcher={
            enableSuggestAlternativesTab
              ? {
                  enabled: true,
                  value: orderTableMode,
                  onChange: handleGlobalAlternativesTabChange,
                }
              : null
          }
          tableProps={{
            ...invoiceTableContainerCommonProps,
            dispensedOrderItems,
            orderTableMode,
            enableEditInvoice,
          }}
        />
      );
    }
  }

  const enableSearchItemsInRegularFlow =
    TABLE_VIEWS.ORDER_PREPARATION === orderTableMode && enableEditInvoice;
  const showSearchItemsInOrderPreparation = isSelfInsuranceOrder
    ? enableSelfInsuranceSearchItemsInOrderPreparation
    : enableSearchItemsInRegularFlow;
  const showInvoiceSummaryAtBottom = isSelfInsuranceOrder
    ? showSelfInsuranceInvoiceSummaryAtBottom
    : TABLE_VIEWS.ORDER_PREPARATION === orderTableMode;

  return (
    <OrderDetailsInvoiceSection>
      {/** Items */}
      {invoiceMainItems}

      {/** Seach Items */}
      {showSearchItemsInOrderPreparation && (
        <OrderDetailsInvoiceSearchBarWrapper id="search-items">
          <OrderDetailsInvoiceSearchBarInputWrapper>
            <OrderDetailsInvoiceSearchBarIcon />
            <OrderDetailsInvoiceSearchBar
              placeholder="Search and add an item"
              value={searchInputQuery}
              onChange={event => handleServicesSuggestionsInputChange(event)}
            />
          </OrderDetailsInvoiceSearchBarInputWrapper>
          <OrderDetailsInvoiceSearchBarEnvelop id="dropdown-list-search-items" open={isEnvelopOpen}>
            <SearchInStock
              stringsData={stringsData}
              orderServicesSuggestions={orderServicesSuggestions}
              handleServicesSuggestionsItemClick={handleServicesSuggestionsItemClick}
              objectMapper={objectMapper}
            />
          </OrderDetailsInvoiceSearchBarEnvelop>
        </OrderDetailsInvoiceSearchBarWrapper>
      )}

      {/** Invoice Summary — original placement; hidden until self-insurance approval is in order state */}
      {showInvoiceSummaryAtBottom && (
        <InvoiceSummary
          stringsData={stringsData}
          themeContext={themeContext}
          orderDetails={orderDetails}
          dispensedOrderItems={dispensedOrderItems}
          showPromoCodeInvoiceText={
            orderDetails.details.promoCode === promoCodeReference &&
            !!orderDetails.details.promoCode
          }
          showPromocodeInvoiceTextOperationSide={
            isPromoCodeInputAvailable &&
            orderStatusToEnableAddingPromoCode?.includes(orderStatus) &&
            selectedFilter === invoiceHeaderMenuItemsObjIntialValue.dispensed
          }
          promoCodeReference={promoCodeReference}
          setPromoCode={setPromoCode}
          selectedFilter={selectedFilter}
          invoiceHeaderMenuItemsObj={invoiceHeaderMenuItemsObjIntialValue}
          orderStatus={orderStatus}
          enableEditInvoice={enableEditInvoice}
          showVezeetaPointsInvoiceText={!!orderReceipt?.vezeetaCash}
        />
      )}
    </OrderDetailsInvoiceSection>
  );
};

OrderInvoice.propTypes = {
  appendProductAlternatives: PropTypes.func,
  setSuggestAllternativeModal: PropTypes.func,
  isEligableForAlternativeRecommendation: PropTypes.bool,
  handleInvoiceItemCheckToBeConfirmed: PropTypes.func,
  checkedItemToBeConfirmed: PropTypes.arrayOf(PropTypes.string),
  orderServicesSuggestions: PropTypes.arrayOf(PropTypes.shape({})),
  dispensedOrderItems: PropTypes.arrayOf(PropTypes.shape({})),
  setDispensedOrderItems: PropTypes.func,
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      noDataPlaceholder: PropTypes.string,
      orderInvoiceFilterOriginal: PropTypes.string,
      orderInvoiceFilterDispensed: PropTypes.string,
      orderInvoiceFilterComparison: PropTypes.string,
      orderInvoiceGridHeader: PropTypes.shape({}),
      orderInvoiceSearchBarPlaceholder: PropTypes.string,
      orderInvoiceBtn: PropTypes.string,
      orderInvoiceFilterAlternatives: PropTypes.string,
      orderInvoiceZeroPriceRequiresAlternativesHint: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      key: PropTypes.string,
      patientInsurance: PropTypes.shape({
        insuranceProviderKey: PropTypes.string,
      }),
      alternativesRecommendations: PropTypes.arrayOf(PropTypes.shape({})),
      orderStateTypeId: PropTypes.number,
      mappedItemsList: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.shape({})),
      ]),

      promoCode: PropTypes.string,
      hasPromoCodeAddedByPatient: PropTypes.bool.isRequired,
      orderReceipt: PropTypes.shape({
        vezeetaCash: PropTypes.number,
      }),
    }),
  }).isRequired,
  getItemsSuggestionsData: PropTypes.func.isRequired,
  resetServicesSuggestionsData: PropTypes.func.isRequired,
  orderStatusToEnableEditInvoice: PropTypes.arrayOf(PropTypes.number).isRequired,
  objectMapper: PropTypes.func.isRequired,
  orderStatusToEnableAddingPromoCode: PropTypes.arrayOf(PropTypes.number),
  productsAlternatives: PropTypes.arrayOf(PropTypes.shape({})),
  orderTableMode: PropTypes.number,
  setOrderTableMode: PropTypes.func,
  onRemoveProductAlternatives: PropTypes.func,
  onMedicalInsuranceDiscountCalculateResult: PropTypes.func,
  insuranceRxLineDiscountLookup: PropTypes.objectOf(
    PropTypes.shape({
      priceBefore: PropTypes.number,
      priceAfter: PropTypes.number,
    }),
  ),
  onMoveInsuranceItemToAdditional: PropTypes.func,
};

OrderInvoice.defaultProps = {
  orderStatusToEnableAddingPromoCode: [],
  onMedicalInsuranceDiscountCalculateResult: null,
  insuranceRxLineDiscountLookup: null,
  onMoveInsuranceItemToAdditional: null,
};

export default OrderInvoice;
